// src/pages/CompaniesPage.jsx
import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Button, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TableSortLabel
} from "@mui/material";
import PageContainer from "../layout/PageContainer";
import useCompanies from "../../hooks/useCompanies";

// ---- Sort-Utils (stabil, Strings case-insensitiv, Zahlen korrekt) ----
function getCellValue(row, key) {
    switch (key) {
        case "name":   return row.name ?? "";
        case "street": return row.street ?? "";
        case "zip":    return row.zip ?? "";
        case "city":   return row.city ?? "";
        case "id":     return row.id ?? null;
        default:       return "";
    }
}
function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    // numerisch versuchen (z.B. PLZ wenn als Zahl geliefert)
    const na = typeof va === "number" ? va : Number.isFinite(Number(va)) ? Number(va) : null;
    const nb = typeof vb === "number" ? vb : Number.isFinite(Number(vb)) ? Number(vb) : null;

    if (na !== null && nb !== null) {
        if (nb < na) return -1;
        if (nb > na) return 1;
        return 0;
    }
    const sa = (va ?? "").toString().toLowerCase();
    const sb = (vb ?? "").toString().toLowerCase();
    if (sb < sa) return -1;
    if (sb > sa) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
        const res = comparator(a[0], b[0]);
        if (res !== 0) return res;
        return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
}

export default function CompaniesPage() {
    const { companies, loading, error, fetchCompanies, createCompany } = useCompanies();

    // Sortierzustand
    const [order, setOrder] = React.useState("asc");        // "asc" | "desc"
    const [orderBy, setOrderBy] = React.useState("name");   // "name" | "street" | "zip" | "city"

    // Dialog / Formular
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({ name:"", street:"", zip:"", city:"" });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    const handleCreate = async () => {
        setSubmitError("");
        if (!form.name.trim()) { setSubmitError("Firmenname ist Pflichtfeld."); return; }
        setSubmitting(true);
        try {
            await createCompany({ ...form, name: form.name.trim() });
            setOpen(false);
            setForm({ name:"", street:"", zip:"", city:"" });
        } catch (e) {
            setSubmitError(e.message || "Erstellen fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    // sortierte Zeilen
    const rows = React.useMemo(() => {
        const list = Array.isArray(companies) ? companies : [];
        return stableSort(list, getComparator(order, orderBy));
    }, [companies, order, orderBy]);

    // Sortier-Handler
    const handleRequestSort = (property) => () => {
        setOrder(prev => (orderBy === property && prev === "asc" ? "desc" : "asc"));
        setOrderBy(property);
    };

    const columns = [
        { id: "name",   label: "Name" },
        { id: "street", label: "Straße" },
        { id: "zip",    label: "PLZ" },
        { id: "city",   label: "Ort" },
    ];

    return (
        <PageContainer
            title="Firmen"
            breadcrumbs={["Home", "Firmen"]}
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchCompanies} disabled={loading}>Aktualisieren</Button>
                    <Button variant="contained" onClick={() => setOpen(true)}>Neue Firma anlegen</Button>
                </Box>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={orderBy === col.id ? order : false}
                                        sx={{ userSelect: "none" }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : "asc"}
                                            onClick={handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(c => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{c.name}</TableCell>
                                    <TableCell>{c.street || "-"}</TableCell>
                                    <TableCell>{c.zip || "-"}</TableCell>
                                    <TableCell>{c.city || "-"}</TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                                        Keine Firmen gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Neue Firma anlegen</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr", sm:"1fr 1fr" }, gap:2, mt:1 }}>
                        <TextField label="Firmenname*" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))}/>
                        <TextField label="Straße" value={form.street} onChange={(e)=>setForm(f=>({...f,street:e.target.value}))}/>
                        <TextField label="PLZ" value={form.zip} onChange={(e)=>setForm(f=>({...f,zip:e.target.value}))}/>
                        <TextField label="Ort" value={form.city} onChange={(e)=>setForm(f=>({...f,city:e.target.value}))}/>
                    </Box>
                    {!!submitError && <Alert severity="error" sx={{ mt:2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={()=>setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
